<template>
  <tile class="opc-zp-aic">
    <foldable v-model="isExpanded" :title="title">
      <template #head>
        <CardHeader :logo="logo" :title="title">
          <template #head-right-content>
            <zm-button v-if="isExpanded" data-testid="checkout-button-zp-aic-plan-details" type="text" @click="onPlanDetailsClick">
              {{ $t('billing.plan_details') }}
            </zm-button>
          </template>
          <div v-if="!isExpanded" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <ul>
              <li v-if="collapsePlan">
                {{ getPlanCollapseText(collapsePlan) }}
              </li>
            </ul>
          </div>
        </CardHeader>
      </template>
      <div class="divide-line"></div>
      <div class="opc-zp-aic__main">
        <zm-form>
          <p class="opc-zp-aic__question">{{ $t('billing.pbx.zp_aic.how_many_minutes_need') }}</p>
          <zm-radio-group
            v-model="selectedPlanCode"
            @update:model-value="onPlanSelect"
            :aria-label="`Select ${PLAN_DISPLAY_NAME} plan tier`"
            class="opc-zp-aic__plan opc-card-radio"
          >
            <zm-radio
              v-for="plan in allPlans"
              :key="plan.plan_code"
              :label="plan.plan_code"
              :disabled="isOptionDisabled(plan)"
              :data-testid="`checkout-radio-${plan.plan_code}`"
            >
              <p class="opc-zp-aic__plan__tier">
                <span>{{ getPlanTierLabel(plan) }}</span>
              </p>
              <div class="opc-zp-aic__plan__price">
                <p class="opc-zp-aic__plan__price--amount">{{ $money(plan.amount) }}</p>
                <p class="opc-zp-aic__plan__price--unit">
                  <span>{{ getPlanPriceUnit(plan) }}</span>
                  <priceVATTip />
                </p>
              </div>
            </zm-radio>
          </zm-radio-group>
        </zm-form>
      </div>
    </foldable>

  </tile>
</template>
<script>
import { get } from 'lodash';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import CardHeader from '@/components/plan-card-header.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { planTypes } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import { planUnitMap } from '@/common/product-unit';
import planMixin from '@/plans/ZOOM_PHONE$AICONCIERGE/mixin';
import planDetailsDialogMixin from '@/plans/ZOOM_PHONE$AICONCIERGE/dialog/plan-details';
import { PLAN_DISPLAY_NAME } from '@/plans/ZOOM_PHONE$AICONCIERGE/constants';

export default {
  mixins: [planMixin, plansMixin, planDetailsDialogMixin],
  data() {
    return {
      isExpanded: true,
      selectedPlanCode: '',
      prevSelectedPlanCode: '',
    };
  },
  components: {
    Foldable,
    Tile,
    CardHeader,
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters({
      getSelectedPlans: 'getSelectedPlans',
      plancodeMapSubscriptionDisabled: 'plancodeMapSubscriptionDisabled',
    }),
    allPlans() {
      const list = this.planMapFlat.filter((item) => item.type === planTypes.ZOOM_PHONE$AICONCIERGE);
      // Sort: Pay as you go first, then prepaid by minutes asc
      return list.slice().sort((a, b) => {
        const aPayg = !!a.is_payg;
        const bPayg = !!b.is_payg;
        if (aPayg !== bPayg) return aPayg ? -1 : 1;
        const aMins = a.minutes || 0;
        const bMins = b.minutes || 0;
        return aMins - bMins;
      });
    },
    cartSelectedPlan() {
      return get(this.getSelectedPlans.filter((p) => p.type === planTypes.ZOOM_PHONE$AICONCIERGE), [0]);
    },
    selectedPlan() {
      return this.allPlans.find((item) => item.plan_code === this.selectedPlanCode) || null;
    },
    collapsePlan() {
      // Prefer current selectedPlan; fallback to store selected plan; then fallback to PAYG as default.
      return this.selectedPlan || this.cartSelectedPlan || this.paygPlan || null;
    },
    paygPlan() {
      return this.allPlans.find((p) => p.is_payg) || null;
    },
  },
  methods: {
    ...mapActions(['switchSelectPlan']),
    ...mapMutations({
      updateSelectPlan: 'updateSelectPlan',
    }),
    onPlanSelect(newPlanCode) {
      const oldPlanCode = this.prevSelectedPlanCode;
      const selectedPlan = this.allPlans.find((p) => p.plan_code === newPlanCode);
      const cartInfo = { selected_number: selectedPlan?.minutes || 1 };

      if (oldPlanCode && oldPlanCode !== newPlanCode) {
        this.switchSelectPlan([oldPlanCode, newPlanCode, cartInfo]);
      } else {
        this.updateSelectPlan({
          plan_code: newPlanCode,
          cartInfo,
        });
      }

      const plan = this.allPlans.find((p) => p.plan_code === newPlanCode);
      if (plan) {
        this.$tracking.buyInteraction(
          'zoom phone add-on',
          `${PLAN_DISPLAY_NAME} - select - ${this.getPlanTierLabel(plan)}`
        );
      }
      this.prevSelectedPlanCode = newPlanCode;
    },
    onPlanDetailsClick() {
      this.showPlanDetailDialog({
        title: this.title,
        trackingPlanName: PLAN_DISPLAY_NAME,
        plans: this.allPlans,
      });
    },
    isOptionDisabled(plan) {
      return !!this.plancodeMapSubscriptionDisabled[plan.plan_code];
    },
    getPlanTierLabel(plan) {
      if (plan?.is_payg) {
        return this.$t('billing.pay_you_go');
      }
      const mins = plan?.minutes;
      if (mins) {
        const minsText = new Intl.NumberFormat().format(mins);
        return `${this.$t('billing.prepaid')} ${minsText} ${this.$t('billing.pbx.zp_aic.mins_abbr')}`;
      }
      return (plan?.plan_code || '').toString();
    },
    getPlanPriceUnit(plan) {
      if (plan?.unit !== planUnitMap.perMinute) {
        return plan?.billing_cycle ? this.$t(`billing.common.per_billing_cycle.${plan.billing_cycle}`) : '';
      }

      const perMin = this.$t('billing.common.per_minute');
      const billedMonthly = this.$t('billing.bill_monthly');
      if (plan?.is_payg) {
        return `${perMin} (${billedMonthly})`;
      }
      const totalAmount = plan?.totalAmount || (plan?.amount || 0) * (plan?.minutes || 0);
      return `${perMin} (${this.$money(totalAmount)} ${billedMonthly})`;
    },
    getPlanBillingCycleLabel(plan) {
      const cycle = plan?.billing_cycle;
      if (!cycle) return '';
      const subscriptionKeyMap = { monthly: 'monthly_subscription', annual: 'annual_subscription' };
      const key = subscriptionKeyMap[cycle] || cycle;
      return this.$t(`billing.billing_cycle.${key}`);
    },
    getPlanCollapseText(plan) {
      if (!plan) return '';
      return `${this.getPlanTierLabel(plan)} • ${this.$money(plan.amount)} • ${this.getPlanBillingCycleLabel(plan)}`;
    },
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? '';
        if (!this.prevSelectedPlanCode && this.selectedPlanCode) {
          this.prevSelectedPlanCode = this.selectedPlanCode;
        }
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `${PLAN_DISPLAY_NAME} - ${val ? 'expand' : 'collapse'}`
        );
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/zoom-card.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/plans/addon-config.scss';

.opc-zp-aic {
  .opc-zm-base-card {
    @include media(mobile) {
      &__name-collapse {
        display: block;
        padding-right: 70px;
      }
    }
    &__name {
      font-style: normal;
      font-weight: 600;
      font-size: 20px;
      line-height: 120%;
      color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
      display: flex;
      flex-direction: row;
      align-items: center;
    }
  }

  &__main {
    margin-top: 0;
    padding-top: 0;
  }

  &__question {
    font-style: normal;
    font-weight: 590;
    font-size: 16px;
    line-height: 20px;
    color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
    margin: 0 0 16px;
  }

  &__disclaimer {
    margin-top: 16px;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--zoom-color-text-neutral, #6e7680);
  }

  &__plan {
    margin-top: 24px;
    color: var(--zoom-color-text-stronger-neutral, #2a2b2d);

    &.opc-card-radio .zbo-radio {
      flex: 0 0 calc(50% - 8px);

      &__wrap {
        width: 100%;
      }

      &__label--inner {
        width: 100%;
      }

      &.is-disabled .opc-zp-aic__plan__price--amount,
      &.is-disabled .opc-zp-aic__plan__price--unit {
        color: var(--zoom-color-state-disable, #adb1b8);
      }
    }

    &__tier {
      font-size: 16px;
      font-style: normal;
      font-weight: 590;
      line-height: 20px;
      color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__price {
      &--amount {
        font-size: 24px;
        font-style: normal;
        font-weight: 590;
        line-height: 28px;
        color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
        margin: 4px 0 2px;
      }

      &--unit {
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
        color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
      }
    }
  }
}
</style>
